import type { TextBlock } from "@prismicio/types-internal/lib/content"
import { RichTextNodeType } from "@prismicio/types-internal/lib/customtypes"
import { Extension } from "@tiptap/core"
import type { Node as ProsemirrorNode } from "prosemirror-model"

export type Direction = "ltr" | "rtl"
export const leftToRight: Direction = "ltr"
export const rightToLeft: Direction = "rtl"

interface TextDirectionOptions {
  supportedExtensionNames: string[]
}

export const attrName = "dir"

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    [attrName]: {
      setDirection: (direction: Direction) => ReturnType
      toggleDirection: (direction: Direction) => ReturnType
      /**
       * Set the text direction attribute
       */
      setRightToLeft: () => ReturnType
      /**
       * Unset the text direction attribute
       */
      setLeftToRight: () => ReturnType
    }
  }
}

export const richTextNodeType = RichTextNodeType.rtl

export const component = Extension.create<TextDirectionOptions>({
  name: "textDirection",

  addOptions() {
    return {
      supportedExtensionNames: [],
    }
  },

  addGlobalAttributes() {
    return [
      {
        types: this.options.supportedExtensionNames,
        attributes: {
          [attrName]: {
            default: leftToRight,
            parseHTML: (element) => element.getAttribute(attrName),
            renderHTML: (attributes) => ({
              [attrName]: getDirectionFrom(attributes[attrName]),
            }),
          },
        },
      },
    ]
  },

  addCommands() {
    return {
      setDirection:
        (direction: Direction) =>
        ({ commands }) => {
          switch (direction) {
            case leftToRight:
              return commands.setLeftToRight()
            case rightToLeft:
              return commands.setRightToLeft()
          }
        },

      toggleDirection:
        (direction: Direction) =>
        ({ commands }) => {
          const oppositeDir =
            direction === rightToLeft ? leftToRight : rightToLeft

          return commands.setDirection(oppositeDir)
        },

      setRightToLeft:
        () =>
        ({ commands }) => {
          return this.options.supportedExtensionNames.every((extName) => {
            const res = commands.updateAttributes(extName, {
              [attrName]: rightToLeft,
            })
            return res
          })
        },

      setLeftToRight:
        () =>
        ({ commands }) => {
          return this.options.supportedExtensionNames.every((extName) =>
            commands.resetAttributes(extName, attrName),
          )
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      "Mod-Shift-r": () => this.editor.commands.setRightToLeft(),
      "Mod-Shift-l": () => this.editor.commands.setLeftToRight(),
    }
  },
})

export const converter = {
  parseNodeAttrsDirection(attributes: Record<string, unknown>): Direction {
    return getDirectionFrom(attributes[attrName])
  },

  fromPrismic(textBlock: TextBlock) {
    return { [attrName]: getDirectionFrom(textBlock.direction) }
  },

  toPrismic(node: ProsemirrorNode): { direction: Direction } {
    return { direction: this.parseNodeAttrsDirection(node.attrs) }
  },
}

function getDirectionFrom(thing: unknown) {
  if (thing === rightToLeft) return rightToLeft
  // In the absence of evidence, assume left to right.
  return leftToRight
}
